<template>
  <div class="pm-root">
    <!-- ── Page header ──────────────────────────────────────────── -->
    <div class="pm-topbar">
      <span class="pm-title">USDT Payout Manager</span>
      <div class="pm-chain-tabs">
        <button v-for="c in CHAINS" :key="c.key"
                class="pm-tab" :class="{ active: network === c.key }"
                :disabled="executing"
                @click="setNetwork(c.key)">{{ c.label }}</button>
      </div>
      <button class="btn btn-sm btn-outline-secondary ml-2"
              :disabled="checkingAll || !allAddresses.length"
              @click="checkAllAml">
        <span v-if="checkingAll" class="pm-spin mr-1"></span>
        {{ checkingAll ? 'Checking…' : '🔍 AML Check All' }}
      </button>
      <div class="pm-queue-tools">
        <input v-model="queueName" class="pm-input pm-queue-name-input" placeholder="Queue name…" style="width:140px" />
        <button class="btn btn-sm btn-outline-success" :disabled="savingQueue || !targets.length" @click="saveCurrentQueue">
          {{ savingQueue ? '…' : '💾 Save' }}
        </button>
        <button class="btn btn-sm btn-outline-secondary" @click="loadQueues">📂 Load</button>
      </div>
    </div>

    <!-- Saved queues dropdown -->
    <div v-if="showQueues" class="pm-queues-panel">
      <div class="pm-queues-header">
        Saved Queues
        <button class="btn btn-xs btn-link ml-auto" @click="showQueues = false">✕</button>
      </div>
      <div v-if="!savedQueues.length" class="pm-queues-empty">No saved queues</div>
      <div v-for="q in savedQueues" :key="q.id" class="pm-queue-item">
        <div class="pm-queue-info">
          <strong>{{ q.name }}</strong>
          <span class="pm-queue-meta">{{ q.network }} · {{ q.schedule_mode }}</span>
        </div>
        <div class="pm-queue-btns">
          <button class="btn btn-xs btn-primary" @click="loadQueueItem(q)">Load</button>
          <button class="btn btn-xs btn-danger" @click="deleteQueueItem(q)">✕</button>
        </div>
      </div>
    </div>

    <!-- ── Body (two-column) ────────────────────────────────────── -->
    <div class="pm-body">

      <!-- LEFT: source + targets panel -->
      <div class="pm-left">

        <!-- SOURCE -->
        <div class="pm-card pm-source-card">
          <div class="pm-card-head">Source Address ({{ chainLabel }})</div>
          <div class="pm-source-row">
            <input v-model.trim="sourceAddress"
                   class="pm-input" placeholder="Paste source address…"
                   :disabled="executing"
                   @blur="onSourceChange" />
            <button class="btn btn-xs btn-outline-secondary ml-1"
                    :disabled="executing"
                    @click="connectLedger">
              {{ ledgerTransport ? '✓ Ledger' : 'Connect Ledger' }}
            </button>
          </div>
          <div v-if="ledgerTransport" class="pm-ledger-info">
            HD index:
            <input type="number" v-model.number="hdIndex" min="0" class="pm-hd-input"
                   :disabled="executing" />
          </div>

          <div class="pm-bal-row">
            <span class="pm-bal-label">USDT:</span>
            <span class="pm-bal-value">{{ sourceBalance || '—' }}</span>
            <span v-if="sourceLoading" class="pm-spin ml-2"></span>
            <button v-else class="btn btn-xs btn-link" @click="refreshSourceBalance">↺</button>
          </div>
          <div class="pm-aml-row">
            <aml-badge v-if="sourceAddress" :address="sourceAddress" :chain="network"
                       ref="sourceAml" @result="onSourceAml" @blocked="onSourceBlocked" />
            <span v-else class="pm-aml-empty">—</span>
          </div>
          <div v-if="sourceAml && sourceAml.hardBlocked" class="pm-blocked-warn">
            ⛔ Source address is HARD BLOCKED — transfers disabled
          </div>
        </div>

        <!-- TARGETS -->
        <div class="pm-card pm-targets-card">
          <div class="pm-card-head">
            Targets ({{ targets.length }})
            <button class="btn btn-xs btn-outline-success float-right"
                    :disabled="executing"
                    @click="addTarget">+ Add</button>
          </div>

          <!-- Bulk paste -->
          <div v-if="showBulk" class="pm-bulk">
            <textarea v-model="bulkText" class="pm-bulk-ta"
                      placeholder="One address per line, or address,amount pairs:&#10;TXZ…abc,500&#10;TXZ…def,250"></textarea>
            <div class="pm-bulk-btns">
              <button class="btn btn-xs btn-primary" @click="parseBulk">Import</button>
              <button class="btn btn-xs btn-outline-secondary ml-1" @click="showBulk = false">Cancel</button>
            </div>
          </div>
          <button v-else class="btn btn-xs btn-link pm-bulk-link"
                  :disabled="executing"
                  @click="showBulk = true">Bulk paste</button>

          <div class="pm-target-list">
            <div v-for="t in targets" :key="t.id"
                 class="pm-target-item" :class="{ 'pm-target-selected': selectedId === t.id }"
                 @click="selectedId = t.id">

              <input v-model.trim="t.address"
                     class="pm-input pm-target-addr"
                     placeholder="Destination address"
                     :disabled="executing"
                     @blur="onTargetAddressChange(t)" />
              <input v-model.trim="t.label"
                     class="pm-input pm-target-label"
                     placeholder="Label (optional)"
                     :disabled="executing" />
              <div class="pm-target-bottom">
                <input v-model.number="t.amount"
                       type="number" min="0" step="any"
                       class="pm-input pm-target-amount"
                       placeholder="0.00"
                       :disabled="executing" />
                <span class="pm-usdt">USDT</span>
                <span class="pm-target-bal" v-if="t.usdtBalance">bal: {{ t.usdtBalance }}</span>
                <span v-if="t.balLoading" class="pm-spin"></span>
                <aml-badge v-if="t.address" :address="t.address" :chain="network" compact
                           @result="r => onTargetAml(t, r)" @blocked="r => onTargetBlocked(t, r)" />
                <button class="btn btn-xs btn-outline-danger ml-1"
                        :disabled="executing"
                        @click.stop="removeTarget(t.id)">✕</button>
              </div>
            </div>
          </div>

          <div v-if="targets.length" class="pm-totals">
            Total: <strong>{{ totalAmount.toFixed(2) }} USDT</strong>
            <span class="pm-totals-balance ml-2" v-if="sourceBalance !== '—'">
              / Source: {{ sourceBalance }}
            </span>
          </div>
        </div>
      </div>

      <!-- RIGHT: graph + queue -->
      <div class="pm-right">
        <div class="pm-graph-wrap">
          <payout-graph
            :source-address="sourceAddress"
            :source-balance="sourceBalance"
            :source-aml="sourceAml"
            :targets="graphTargets"
            :selected-id="selectedId"
            @select="selectedId = $event" />
        </div>
        <div class="pm-queue-wrap">
          <payout-queue
            :transfers="queueTransfers"
            :log="transferLog"
            :executing="executing"
            :mode="scheduleMode"
            :chain="network"
            @execute-all="executeAll"
            @send-now="id => sendNow(id)"
            @remove="id => removeTarget(id)"
            @select="id => (selectedId = id)"
            @update-amount="({ id, amount }) => updateAmount(id, amount)"
            @update-schedule="updateSchedule"
            @update-mode="scheduleMode = $event" />
        </div>
      </div>
    </div>

    <!-- Ledger prompt overlay -->
    <div v-if="ledgerPrompt" class="pm-overlay">
      <div class="pm-prompt-card">
        <div class="pm-prompt-icon">🔐</div>
        <div class="pm-prompt-title">Confirm on Ledger</div>
        <div class="pm-prompt-msg">{{ ledgerPrompt }}</div>
        <div class="pm-prompt-hint">Physically press both buttons on your Ledger device to approve.</div>
      </div>
    </div>
  </div>
</template>

<script>
import AmlBadge   from '../components/AmlBadge.vue';
import PayoutGraph from '../components/PayoutGraph.vue';
import PayoutQueue from '../components/PayoutQueue.vue';
import { fetchUsdtBalance, fmtUsdt, CHAIN_LABEL } from '../services/usdtService';
import { checkAddresses, CHAIN_AML }               from '../services/amlService';
import { sendUsdt, openLedgerTransport }            from '../services/transferService';
import { saveTransfer, saveQueue, getQueues, deleteQueue } from '../services/pbService';

const CHAINS = [
  { key: 'tron',     label: 'TRC20' },
  { key: 'ethereum', label: 'ERC20' },
  { key: 'bsc',      label: 'BEP20' },
  { key: 'polygon',  label: 'POL'   },
];

let _uid = 1;
function newId() { return 't' + (_uid++); }

export default {
  name: 'PayoutManager',
  components: { AmlBadge, PayoutGraph, PayoutQueue },

  data() {
    return {
      CHAINS,
      network:        'tron',
      sourceAddress:  '',
      sourceBalance:  '—',
      sourceLoading:  false,
      sourceAml:      null,
      hdIndex:        0,
      ledgerTransport:null,
      targets:        [],
      selectedId:     null,
      scheduleMode:   'manual',
      executing:      false,
      transferLog:    [],
      checkingAll:    false,
      ledgerPrompt:   null,
      showBulk:       false,
      bulkText:       '',
      savedQueues:    [],
      showQueues:     false,
      queueName:      '',
      savingQueue:    false,
    };
  },

  computed: {
    chainLabel() { return CHAIN_LABEL[this.network] || this.network; },
    blockchain()  { return CHAIN_AML[this.network] || 'TRX'; },

    totalAmount() {
      return this.targets.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
    },

    allAddresses() {
      const arr = [];
      if (this.sourceAddress) arr.push({ address: this.sourceAddress, blockchain: this.blockchain });
      this.targets.forEach(t => {
        if (t.address) arr.push({ address: t.address, blockchain: this.blockchain });
      });
      return arr;
    },

    graphTargets() {
      return this.targets.map(t => ({
        id:          t.id,
        address:     t.address,
        amount:      t.amount,
        usdtBalance: t.usdtBalance,
        amlResult:   t.amlResult,
        status:      t.status || 'pending',
      }));
    },

    queueTransfers() {
      return this.targets.map(t => ({
        id:           t.id,
        address:      t.address,
        label:        t.label,
        amount:       t.amount,
        amlResult:    t.amlResult,
        status:       t.status || 'pending',
        scheduledAt:  t.scheduledAt,
        delayMinutes: t.delayMinutes,
        rotationSlot: t.rotationSlot,
        txid:         t.txid,
      }));
    },
  },

  methods: {
    setNetwork(key) {
      this.network = key;
      this.sourceBalance = '—';
      this.sourceAml = null;
      this.targets.forEach(t => { t.amlResult = null; t.usdtBalance = null; });
      if (this.sourceAddress) this.refreshSourceBalance();
    },

    async connectLedger() {
      try {
        if (this.ledgerTransport) { this.ledgerTransport.close(); this.ledgerTransport = null; }
        this.ledgerTransport = await openLedgerTransport();
        this.addLog('success', 'Ledger connected');
      } catch (e) {
        this.addLog('error', 'Ledger connection failed: ' + e.message);
      }
    },

    onSourceChange() {
      if (this.sourceAddress) this.refreshSourceBalance();
    },

    async refreshSourceBalance() {
      if (!this.sourceAddress) return;
      this.sourceLoading = true;
      try {
        const res = await fetchUsdtBalance(this.sourceAddress, this.network);
        this.sourceBalance = fmtUsdt(res.balance);
      } catch (_) {
        this.sourceBalance = '—';
      } finally {
        this.sourceLoading = false;
      }
    },

    onSourceAml(result)   { this.sourceAml = result; },
    onSourceBlocked(r) {
      if (r.hardBlocked) this.addLog('error', `Source address is HARD BLOCKED (${r.risk_level})`);
      else this.addLog('error', `Source address is SOFT BLOCKED (${r.risk_level})`);
    },

    addTarget() {
      this.targets.push({
        id: newId(), address: '', label: '', amount: '',
        amlResult: null, status: 'pending', usdtBalance: null,
        balLoading: false, scheduledAt: null, delayMinutes: 0,
        rotationSlot: '', txid: null,
      });
    },

    removeTarget(id) {
      const idx = this.targets.findIndex(t => t.id === id);
      if (idx !== -1) this.targets.splice(idx, 1);
      if (this.selectedId === id) this.selectedId = null;
    },

    async onTargetAddressChange(t) {
      if (!t.address) return;
      t.balLoading = true;
      try {
        const res = await fetchUsdtBalance(t.address, this.network);
        t.usdtBalance = fmtUsdt(res.balance);
      } catch (_) {
        t.usdtBalance = null;
      } finally {
        t.balLoading = false;
      }
    },

    onTargetAml(t, result) { t.amlResult = result; },
    onTargetBlocked(t, r) {
      t.status = 'blocked';
      this.addLog('error', `Target ${this.short(t.address)} BLOCKED (${r.risk_level})`);
    },

    updateAmount(id, amount) {
      const t = this.targets.find(x => x.id === id);
      if (t) t.amount = amount;
    },

    updateSchedule({ id, scheduledAt, delayMinutes, rotationSlot }) {
      const t = this.targets.find(x => x.id === id);
      if (!t) return;
      if (scheduledAt  !== undefined) t.scheduledAt  = scheduledAt;
      if (delayMinutes !== undefined) t.delayMinutes = delayMinutes;
      if (rotationSlot !== undefined) t.rotationSlot = rotationSlot;
    },

    async checkAllAml() {
      this.checkingAll = true;
      try {
        const entries = this.allAddresses;
        const map = await checkAddresses(entries);
        // Update source
        if (this.sourceAddress) {
          const k = `${this.blockchain}:${this.sourceAddress.toLowerCase()}`;
          if (map.has(k)) this.sourceAml = map.get(k);
        }
        // Update targets
        this.targets.forEach(t => {
          if (!t.address) return;
          const k = `${this.blockchain}:${t.address.toLowerCase()}`;
          if (map.has(k)) {
            t.amlResult = map.get(k);
            if (t.amlResult.hardBlocked || t.amlResult.softBlocked) t.status = 'blocked';
          }
        });
      } finally {
        this.checkingAll = false;
      }
    },

    // ── Queue execution ──────────────────────────────────────────

    async executeAll() {
      if (!this.ledgerTransport) {
        this.addLog('error', 'Connect Ledger first');
        return;
      }
      if (this.sourceAml && this.sourceAml.hardBlocked) {
        this.addLog('error', 'Source is hard-blocked — cannot execute');
        return;
      }
      this.executing = true;
      let prevConfirmTime = null;

      for (const t of this.targets) {
        if (!['pending', 'scheduled', 'failed'].includes(t.status)) continue;
        if (t.amlResult && t.amlResult.hardBlocked) {
          this.addLog('error', `Skipping ${this.short(t.address)} — hard blocked`);
          continue;
        }
        if (!t.address || !t.amount) continue;

        // Handle scheduling
        await this._waitForSchedule(t, prevConfirmTime);
        if (!this.executing) break;

        try {
          await this._executeSingle(t);
          prevConfirmTime = Date.now();
        } catch (_) {
          // error already logged inside _executeSingle
        }
      }

      this.ledgerPrompt = null;
      this.executing = false;
    },

    async sendNow(id) {
      if (!this.ledgerTransport) {
        this.addLog('error', 'Connect Ledger first');
        return;
      }
      const t = this.targets.find(x => x.id === id);
      if (!t) return;
      if (t.amlResult && t.amlResult.hardBlocked) {
        this.addLog('error', 'Address is hard-blocked');
        return;
      }
      this.executing = true;
      try {
        await this._executeSingle(t);
      } catch (_) {}
      this.ledgerPrompt = null;
      this.executing = false;
    },

    async _waitForSchedule(t, prevConfirmTime) {
      if (this.scheduleMode === 'timed' && t.scheduledAt) {
        const diff = t.scheduledAt - Date.now();
        if (diff > 0) {
          t.status = 'scheduled';
          await new Promise(res => setTimeout(res, diff));
        }
      } else if (this.scheduleMode === 'delay' && t.delayMinutes > 0 && prevConfirmTime) {
        const waitUntil = prevConfirmTime + t.delayMinutes * 60000;
        const remaining = waitUntil - Date.now();
        if (remaining > 0) {
          t.status = 'scheduled';
          await new Promise(res => setTimeout(res, remaining));
        }
      }
    },

    async _executeSingle(t) {
      t.status = 'active';
      try {
        this.ledgerPrompt = `Sending ${t.amount} USDT → ${t.address}`;
        const result = await sendUsdt({
          chain:       this.network,
          fromAddress: this.sourceAddress,
          toAddress:   t.address,
          amount:      t.amount,
          hdIndex:     this.hdIndex,
          transport:   this.ledgerTransport,
          onStatus: (type, msg) => {
            this.ledgerPrompt = msg;
            if (type === 'broadcast') t.status = 'broadcasting';
          },
        });

        t.txid   = result.txid;
        t.status = 'confirmed';
        this.addLog('success', `Sent ${t.amount} USDT → ${this.short(t.address)}`, result.txid, t);
        await this.refreshSourceBalance();
        // Persist to DB (non-blocking)
        saveTransfer({
          txid:         result.txid,
          from_address: this.sourceAddress,
          to_address:   t.address,
          amount:       parseFloat(t.amount) || 0,
          chain:        this.network,
          status:       'confirmed',
          notes:        t.label || '',
        }).catch(() => {});
      } catch (e) {
        t.status = 'failed';
        this.addLog('error', `Failed ${this.short(t.address)}: ${e.message}`);
        this.ledgerPrompt = null;
        throw e;
      }
    },

    addLog(type, message, txid, target) {
      this.transferLog.push({
        type,
        message,
        txid: txid || null,
        timestamp: Date.now(),
        chain: this.network,
        targetId: target ? target.id : null,
      });
    },

    parseBulk() {
      const lines = this.bulkText.trim().split(/\n/);
      lines.forEach(line => {
        const parts = line.split(/[,\t]/).map(s => s.trim());
        if (!parts[0]) return;
        this.targets.push({
          id: newId(), address: parts[0], label: '',
          amount: parts[1] ? parseFloat(parts[1]) : '',
          amlResult: null, status: 'pending', usdtBalance: null,
          balLoading: false, scheduledAt: null, delayMinutes: 0, rotationSlot: '', txid: null,
        });
        if (parts[0]) this.onTargetAddressChange(this.targets[this.targets.length - 1]);
      });
      this.bulkText = '';
      this.showBulk = false;
    },

    short(addr) {
      if (!addr) return '—';
      return addr.length > 14 ? addr.slice(0, 7) + '…' + addr.slice(-5) : addr;
    },

    async saveCurrentQueue() {
      if (!this.targets.length) return;
      const name = this.queueName || `Queue ${new Date().toLocaleDateString()}`;
      this.savingQueue = true;
      try {
        await saveQueue({
          name,
          network:        this.network,
          source_address: this.sourceAddress,
          hd_index:       this.hdIndex,
          schedule_mode:  this.scheduleMode,
          status:         'draft',
        });
        this.queueName = '';
        alert('Queue saved.');
      } catch (e) {
        alert('Save failed: ' + e.message);
      } finally {
        this.savingQueue = false;
      }
    },

    async loadQueues() {
      try {
        const res = await getQueues();
        this.savedQueues = res.items;
        this.showQueues = true;
      } catch (e) {
        alert('Load failed: ' + e.message);
      }
    },

    loadQueueItem(q) {
      this.network       = q.network || 'tron';
      this.sourceAddress = q.source_address || '';
      this.hdIndex       = q.hd_index || 0;
      this.scheduleMode  = q.schedule_mode || 'manual';
      this.showQueues    = false;
    },

    async deleteQueueItem(q) {
      if (!confirm(`Delete queue "${q.name}"?`)) return;
      try {
        await deleteQueue(q.id);
        this.savedQueues = this.savedQueues.filter(s => s.id !== q.id);
      } catch (e) {
        alert('Delete failed: ' + e.message);
      }
    },
  },
};
</script>

<style scoped>
.pm-root {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #0d0d0d;
  color: #ccc;
  font-size: .85rem;
  overflow: hidden;
}

/* Top bar */
.pm-topbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: #111;
  border-bottom: 1px solid #222;
  flex-wrap: wrap;
}
.pm-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #f0b90b;
  white-space: nowrap;
}
.pm-chain-tabs { display: flex; gap: 4px; }
.pm-tab {
  background: #1e1e1e; color: #888; border: 1px solid #333;
  border-radius: 4px; padding: 3px 10px; font-size: .78rem; cursor: pointer;
  transition: background .15s;
}
.pm-tab.active  { background: #2a2a10; color: #f0b90b; border-color: #f0b90b; }
.pm-tab:hover   { background: #252525; }

.pm-queue-tools { display: flex; align-items: center; gap: 6px; margin-left: auto; }
.pm-queue-name-input { font-size: 0.78rem; padding: 3px 8px; height: 30px; background: #1e1e1e; border: 1px solid #333; border-radius: 4px; color: #ccc; }

.pm-queues-panel {
  background: #111; border-bottom: 1px solid #2a2a2a;
  padding: 10px 16px; max-height: 220px; overflow-y: auto;
}
.pm-queues-header {
  display: flex; align-items: center; font-size: 0.78rem; font-weight: 700;
  color: #888; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 8px;
}
.pm-queues-empty { font-size: 0.8rem; color: #555; padding: 8px 0; }
.pm-queue-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 7px 0; border-bottom: 1px solid #1e1e1e;
}
.pm-queue-info { display: flex; flex-direction: column; gap: 2px; }
.pm-queue-info strong { font-size: 0.84rem; color: #ccc; }
.pm-queue-meta { font-size: 0.72rem; color: #555; }
.pm-queue-btns { display: flex; gap: 6px; }

/* Body */
.pm-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Left panel */
.pm-left {
  width: 300px;
  min-width: 260px;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-y: auto;
  border-right: 1px solid #222;
  background: #111;
}

.pm-card {
  padding: 12px;
  border-bottom: 1px solid #1e1e1e;
}
.pm-card-head {
  font-size: .78rem;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: .06em;
  margin-bottom: 8px;
}

.pm-source-row { display: flex; align-items: center; margin-bottom: 6px; }
.pm-ledger-info { font-size: .72rem; color: #666; margin-bottom: 6px; display: flex; align-items: center; gap: 6px; }
.pm-hd-input { width: 46px; background: #1a1a1a; border: 1px solid #333; border-radius: 3px; color: #fff; padding: 1px 4px; font-size: .78rem; }

.pm-bal-row { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.pm-bal-label { color: #666; font-size: .75rem; }
.pm-bal-value { color: #fff; font-weight: 600; }

.pm-aml-row { margin-bottom: 4px; }
.pm-aml-empty { color: #444; font-size: .75rem; }
.pm-blocked-warn {
  font-size: .75rem; color: #dc3545; background: rgba(220,53,69,.1);
  border-radius: 4px; padding: 4px 6px; margin-top: 4px;
}

.pm-input {
  background: #1a1a1a; border: 1px solid #333; border-radius: 4px;
  color: #fff; padding: 4px 8px; font-size: .8rem; width: 100%;
}
.pm-input:focus { outline: none; border-color: #555; }

.pm-targets-card { flex: 1; display: flex; flex-direction: column; }
.pm-bulk-link { font-size: .72rem; color: #555; padding: 0; }
.pm-bulk { margin-bottom: 6px; }
.pm-bulk-ta {
  width: 100%; height: 70px; background: #1a1a1a; border: 1px solid #333;
  border-radius: 4px; color: #ccc; font-size: .75rem; padding: 4px 6px; resize: none;
}
.pm-bulk-btns { margin-top: 4px; display: flex; }

.pm-target-list { overflow-y: auto; flex: 1; }
.pm-target-item {
  border: 1px solid #1e1e1e; border-radius: 5px; padding: 7px;
  margin-bottom: 6px; cursor: pointer; transition: border-color .15s;
}
.pm-target-item:hover { border-color: #333; }
.pm-target-selected { border-color: #f0b90b !important; }

.pm-target-addr  { margin-bottom: 4px; }
.pm-target-label { margin-bottom: 4px; font-size: .72rem; }
.pm-target-bottom { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.pm-target-amount { width: 70px; text-align: right; }
.pm-usdt  { color: #888; font-size: .72rem; }
.pm-target-bal { color: #666; font-size: .68rem; }

.pm-totals {
  font-size: .78rem; color: #aaa; padding-top: 6px; border-top: 1px solid #1e1e1e;
}
.pm-totals strong { color: #f0b90b; }
.pm-totals-balance { color: #555; }

/* Right panel */
.pm-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.pm-graph-wrap { flex: 0 0 50%; min-height: 220px; overflow: hidden; border-bottom: 1px solid #1e1e1e; }
.pm-queue-wrap { flex: 1; overflow: hidden; display: flex; flex-direction: column; }

/* Ledger overlay */
.pm-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.7);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
}
.pm-prompt-card {
  background: #1e1e1e; border: 1px solid #444; border-radius: 10px;
  padding: 32px 40px; text-align: center; max-width: 380px;
}
.pm-prompt-icon { font-size: 2.5rem; margin-bottom: 12px; }
.pm-prompt-title { font-size: 1.1rem; font-weight: 700; color: #f0b90b; margin-bottom: 8px; }
.pm-prompt-msg   { color: #ddd; margin-bottom: 8px; word-break: break-all; }
.pm-prompt-hint  { font-size: .78rem; color: #666; }

/* Spinner */
.pm-spin {
  display: inline-block; width: 10px; height: 10px;
  border: 2px solid rgba(255,255,255,.2); border-top-color: #ccc;
  border-radius: 50%; animation: pmSpin .7s linear infinite; vertical-align: middle;
}
@keyframes pmSpin { to { transform: rotate(360deg); } }

/* btn-xs helper */
.btn-xs { padding: 1px 7px; font-size: .72rem; }
</style>
